<template>
  <div>
    <b-table
      class="list-table efficiency-tests-table mb-0"
      hover
      striped
      no-local-sorting
      no-sort-reset
      :fields="visibleFields"
      :tbody-tr-class="['']"
      :items="items"
      @sort-changed="onSortingChanged"
    >
      <template v-slot:cell(name)="{ item }">
        <div v-if="item.name">
          <span> {{ item.name }}</span>
        </div>
        <div v-else>
          <span>{{ $t('efficiencyTestsList.efficiencyTestsListTable.customProfile') }}</span>
        </div>
      </template>

      <template v-slot:cell(scoringProfileModelName)="{ item }">
        <div class="model-name text-truncate" :title="item.scoringProfileModelName">
          {{ item.scoringProfileModelName }}
        </div>
      </template>

      <template v-slot:cell(profileCoverage)="{ item }">
        <div>
          <span class="mr-2">{{ $t('efficiencyTestsList.efficiencyTestsListTable.coverage.primaryMarket') }}:</span>
          <span>{{ item.primaryMarketIsinCount }}</span>
        </div>
        <div>
          <span class="mr-2">{{ $t('efficiencyTestsList.efficiencyTestsListTable.coverage.secondaryMarket') }}:</span>
          <span>{{ item.secondaryMarketIsinCount }}</span>
        </div>
      </template>

      <template v-slot:cell(status)="{ item }">
        <!-- One line: in the narrow status column of the product tab the icon would otherwise wrap under the label. -->
        <div class="d-inline-flex align-items-center text-nowrap">
          <efficiency-test-status :status="item.status"></efficiency-test-status>
          <failure-icon
            v-if="item.status === 'Failed'"
            clickable
            :type="item.lastErrorIsBlocking ? 'blocking' : 'transient'"
            :text="$tc('efficiencyTestFailures.listTooltip', item.failureCount || 0, { n: item.failureCount || 0 })"
            @click="failuresTest = item"
          ></failure-icon>
        </div>
      </template>
      <template v-slot:cell(outcome)="{ item }">
        <efficiency-test-outcome :outcome="item.outcome"></efficiency-test-outcome>
      </template>
      <template v-slot:cell(insight)="{ item }">
        <router-link v-if="item.outcome" :to="goToInsight(item.scoringProfileId)">
          <span class="mr-2">{{ $t('efficiencyTestsList.efficiencyTestsListTable.results') }}</span>
          <i class="fas fa-long-arrow-right"></i>
        </router-link>
        <operation-progress
          v-else-if="follower.progressOf(item.scoringProfileId)"
          :progress="follower.progressOf(item.scoringProfileId)"
        ></operation-progress>
        <a v-else-if="executeCommand(item)" href="#" @click.prevent="confirmExecuteTest(item)">
          <span class="mr-2">{{ $t('efficiencyTestsList.efficiencyTestsListTable.' + executeCommand(item)) }}</span>
          <i class="fas fa-sync-alt"></i>
        </a>
      </template>
      <template v-slot:cell(actions)="{ item }">
        <div class="text-right">
          <b-dropdown size="md" toggle-class="text-decoration-none button-primary-class p-3" right no-caret>
            <template #button-content>
              <i class="fas fa-ellipsis-v"></i>
              <span class="sr-only">{{ $t('efficiencyTestsList.efficiencyTestsListTable.columns.actions') }}</span>
            </template>
            <b-dropdown-item-button
              v-if="isEditable(item)"
              @click="goToEditingProfile(item.scoringProfileId, 'update')"
            >
              <small>{{ $t('efficiencyTestsList.efficiencyTestsListActions.update') }}</small>
            </b-dropdown-item-button>
            <b-dropdown-item-button @click="goToEditingProfile(item.scoringProfileId, 'duplicate', item)">
              <small>{{ $t('efficiencyTestsList.efficiencyTestsListActions.duplicate') }}</small>
            </b-dropdown-item-button>
            <b-dropdown-item-button v-if="canDelete(item)" @click="confirmDeleteTest(item)">
              <small>{{ $t('efficiencyTestsList.efficiencyTestsListActions.delete') }}</small>
            </b-dropdown-item-button>
          </b-dropdown>
        </div>
      </template>
    </b-table>
    <efficiency-test-failures-modal
      :test="failuresTest"
      @close="failuresTest = null"
    ></efficiency-test-failures-modal>
  </div>
</template>

<script>
// Efficiency tests table + row actions, shared by EfficiencyTestsList.vue (full list, every column)
// and ProductEfficiencyTests.vue (product detail page, a subset via `columns`). Owns no list state:
// it emits `sort-changed` (API sort value) and `refresh` (after execute/delete) and the host reloads
// its own data, so the product widget never touches the list page's filters/pagination.
import { createNamespacedHelpers } from 'vuex'
import EfficiencyTestOutcome from './EfficiencyTestOutcome.vue'
import EfficiencyTestStatus from './EfficiencyTestStatus.vue'
import OperationProgress from '../Lib/OperationProgress.vue'
import FailureIcon from './FailureIcon.vue'
import EfficiencyTestFailuresModal from './EfficiencyTestFailuresModal.vue'
import { createExecutionFollower } from '../../utils/executionFollower'
import efficiencyTestExecuteRejection from '../../mixins/efficiencyTestExecuteRejection'
import { testCommand } from '../../utils/testCommand'
import { duplicateRouteQuery } from '../../utils/efficiencyTestEditTabs'
import { formatDate } from '../../utils/formatter'
const { mapActions } = createNamespacedHelpers('efficiencyTestsList')

export default {
  components: {
    EfficiencyTestOutcome,
    EfficiencyTestStatus,
    OperationProgress,
    FailureIcon,
    EfficiencyTestFailuresModal,
  },

  mixins: [efficiencyTestExecuteRejection],

  // Before the options of the component are set up: a watcher that runs at once (`immediate`) already adopts
  // the executions under way.
  beforeCreate() {
    this.follower = createExecutionFollower({ onEnded: () => this.refreshAfterExecution() })
  },

  beforeDestroy() {
    this.follower.stopAll()
  },

  data() {
    return {
      // The Failed test whose reasons are open in the modal.
      failuresTest: null,
    }
  },

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    // Field keys to show, in the table's own order; null = all.
    columns: {
      type: Array,
      default: null,
    },
    sortable: {
      type: Boolean,
      default: true,
    },
  },

  watch: {
    // A test already in progress when the list is loaded is followed from where it is.
    items: {
      immediate: true,
      handler(items) {
        this.follower.adopt(items)
      },
    },
  },

  computed: {
    fields() {
      return [
        {
          key: 'name',
          sortKey: 'Name',
          class: ['name', 'align-middle', 'font-weight-bold'],
          sortable: true,
          label: this.$t('efficiencyTestsList.efficiencyTestsListTable.columns.name'),
        },
        {
          key: 'testedProductIsin',
          sortKey: 'Isin',
          class: ['isin', 'align-middle'],
          sortable: true,
          label: this.$t('common.isin'),
        },
        {
          key: 'scoringProfileModelName',
          sortKey: 'ProfileModel',
          class: ['profileModel', 'align-middle'],
          sortable: true,
          label: this.$t('efficiencyTestsList.efficiencyTestsListTable.columns.scoringDefinition'),
        },
        {
          key: 'profileCoverage',
          sortable: false,
          class: ['profileCoverage', 'align-middle'],
          label: this.$t('efficiencyTestsList.efficiencyTestsListTable.columns.coverage'),
        },
        {
          key: 'lastUpdatedBy',
          sortKey: 'LastUpdatedBy',
          class: ['user', 'align-middle'],
          sortable: true,
          label: this.$t('efficiencyTestsList.efficiencyTestsListTable.columns.user'),
        },
        {
          key: 'lastUpdatedAt',
          sortKey: 'LastUpdatedAt',
          class: ['last-edited-at', 'align-middle'],
          formatter: (value) => {
            return value ? formatDate(value) : '-'
          },
          sortable: true,
          label: this.$t('efficiencyTestsList.efficiencyTestsListTable.columns.lastEdit'),
        },
        {
          key: 'status',
          sortKey: 'Status',
          sortable: true,
          class: ['status', 'align-middle'],
          label: this.$t('efficiencyTestsList.efficiencyTestsListTable.columns.status'),
        },
        {
          key: 'outcome',
          sortKey: 'Outcome',
          sortable: true,
          class: ['outcome', 'align-middle'],
          label: this.$t('efficiencyTestsList.efficiencyTestsListTable.columns.outcome'),
        },
        {
          key: 'insight',
          sortable: false,
          class: ['insight', 'align-middle'],
          label: this.$t('efficiencyTestsList.efficiencyTestsListTable.columns.insight'),
        },
        {
          key: 'actions',
          sortable: false,
          label: this.$t('efficiencyTestsList.efficiencyTestsListTable.columns.actions'),
          class: ['actions', 'align-middle'],
        },
      ]
    },

    visibleFields() {
      const fields = this.columns ? this.fields.filter((f) => this.columns.includes(f.key)) : this.fields
      return this.sortable ? fields : fields.map((f) => ({ ...f, sortable: false }))
    },
  },

  methods: {
    ...mapActions(['deleteScoringProfile', 'executeEfficiencyTest']),

    onSortingChanged(data) {
      this.$emit('sort-changed', data.sortDesc ? '-' + data.sortBy : data.sortBy)
    },

    goToInsight(id) {
      const routeData = this.$router.resolve({
        name: 'efficiencyTestsDetails',
        params: { scoringProfileId: id.toString() },
      })
      // The path below the router base, as <router-link :to> expects it: `.href` already carries the
      // /{assetClass}/ prefix, which the link would then add a second time.
      return routeData.route.fullPath
    },

    // Confirmation box whose sentence carries the test name in bold: the translated sentence is
    // split on a marker put in place of {name}, then rebuilt as VNodes (no v-html, no inline styles).
    confirmWithTestName(item, messageKey, options) {
      const marker = '[[name]]'
      const [before, after] = this.$t(messageKey, { name: marker }).split(marker)
      const h = this.$createElement
      const message = h('p', { class: 'mb-0' }, [before, h('strong', item.name), after])

      return this.$bvModal.msgBoxConfirm([message], {
        cancelTitle: this.$t('common.cancel'),
        centered: true,
        ...options,
      })
    },

    confirmExecuteTest(item) {
      this.confirmWithTestName(item, 'efficiencyTestsList.modalMessages.confirmExecute', {
        title: this.$t('efficiencyTestsList.modalMessages.confirmExecuteTitle'),
        okTitle: this.$t('efficiencyTestsList.efficiencyTestsListTable.' + this.executeCommand(item)),
        okVariant: 'primary',
        cancelVariant: 'outline-primary',
      }).then((confirmed) => {
        if (confirmed) {
          this.executeTest(item.scoringProfileId)
        }
      })
    },

    executeTest(id) {
      this.$store.dispatch('efficiencyTestsCommon/setLoading', true)
      return this.executeEfficiencyTest(id)
        .then((started) => {
          this.follower.track(id, started && started.operationId)
          this.refreshAfterExecution()
        })
        .catch((err) => this.handleExecuteRejection(err))
        .finally(() => {
          this.$store.dispatch('efficiencyTestsCommon/setLoading', false)
        })
    },

    // The host reloads what it shows, which is where the test turns from in progress to its outcome.
    refreshAfterExecution() {
      this.$emit('refresh')
    },

    // A Failed test duplicated opens on the tab where its reasons are.
    goToEditingProfile(id, action, test) {
      this.$store.dispatch('efficiencyTestsCommon/setIsDuplicate', action === 'duplicate')
      this.$store.dispatch('efficiencyTestsCommon/setScoringProfileId', id)
      this.$router.push({
        name: 'efficiencyTestsEdit',
        params: { id: action === 'update' ? id.toString() : 'copy_' + id },
        query: action === 'duplicate' ? duplicateRouteQuery(test) : undefined,
      })
    },

    confirmDeleteTest(item) {
      this.confirmWithTestName(item, 'efficiencyTestsList.modalMessages.confirmDelete', {
        title: this.$t('efficiencyTestsList.modalMessages.confirmDeleteTitle'),
        okTitle: this.$t('common.remove'),
        okVariant: 'danger',
        cancelVariant: 'outline-secondary',
      }).then((confirmed) => {
        if (confirmed) {
          this.deleteScoringProfile(item.scoringProfileId)
            .then(() => this.$emit('refresh'))
            .catch((err) => {
              this.httpErrorsHandler(err)
            })
        }
      })
    },

    // Only a Created test is edited in place: one that has run or failed is duplicated (testCommand).
    isEditable(item) {
      return testCommand(item).edit
    },

    executeCommand(item) {
      return testCommand(item).execute
    },
    canDelete(item) {
      return testCommand(item).delete
    },

  },
}
</script>

<style lang="scss">
.button-primary-class,
.button-primary-class:active {
  border: 0;
  color: var(--secondaryColor) !important;
  background-color: transparent !important;
}

.show > .button-primary-class {
  color: var(--secondaryColor) !important;
}

.model-name {
  max-width: 240px;
}

// Scoped to this table: the same selector in the other lists is global CSS, and whichever file loads
// last would otherwise decide the column widths of all of them.
table.list-table.efficiency-tests-table {
  th,
  td {
    &.name {
      width: 30%;
    }
    &.profileModel {
      max-width: 240px;
    }
    &.insight {
      width: 10%;
    }
    &.actions {
      width: 3%;
    }
    &.profileCoverage {
      width: 9%;
    }
    &.user {
      width: 10%;
    }
    &.status {
      width: 7%;
    }
    &.outcome {
      width: 6%;
    }
  }
}
</style>
